import React from "react";
import CircularProgress from "@mui/material/CircularProgress";
import { usePublisherAndContacts, usePublisherGuidelines } from "@seabrookstudios/pitch2table";
import { EmptyDetailCard } from "../../core/EmptyDetailCard";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import Typography from "@mui/material/Typography";
import { StaticListItemUrl } from "../../core/EditableListItemUrl";
import { IsMember, LockedListItem } from "../../core/HasEntitlements";
import { Now } from "../../core/Now";
import { SupportEmail } from "../../core/CommonLinks";
import { isStale } from "@seabrookstudios/pitch2table-core";
import { PublisherTimezoneWarning } from "../../games/PublisherTimezoneWarning";
import Stack from "@mui/material/Stack";
import Markdown from "react-markdown";

/**
 * @param {object} props
 * @param {import("@seabrookstudios/pitch2table-core").PublisherForApp} props.publisher
 */
const PublisherResearchDetailCard = ({ publisher }) => {
  const { data: guidelines } = usePublisherGuidelines(publisher.id);

  return (
    <>
      {publisher.timezone && <PublisherTimezoneWarning timezone={publisher.timezone} />}
      <Box pt={2}>
        <Typography variant="h5" textAlign="left" color="text.secondary" noWrap>
          {publisher.name}
        </Typography>
        {publisher.alternateNames.length > 0 && (
          <ListItem>
            <ListItemText primary="Also Known As" secondary={publisher.alternateNames.join(", ")} />
          </ListItem>
        )}
      </Box>
      <List>
        {publisher.defunct && (
          <Alert severity="error">
            Based on our latest information, this publisher is believed to be defunct. If this is incorrect please
            contact <SupportEmail />.
          </Alert>
        )}
        <IsMember LockedView={<LockedListItem title="Accepting Submissions" />}>
          {publisher.acceptingSubmissions === false && publisher.defunct !== true && (
            <Alert severity="warning">Publisher is currently not accepting submissions.</Alert>
          )}
        </IsMember>
        {publisher.website && <StaticListItemUrl title="Website" text={publisher.website} />}
        {publisher.contact && (
          <IsMember LockedView={<LockedListItem title="Contact Information" />}>
            {publisher.contact.includes("@") ? (
              <ListItem>
                <ListItemText primary="Contact" secondary={publisher.contact} />
              </ListItem>
            ) : (
              <StaticListItemUrl title="Contact" text={publisher.contact} />
            )}
          </IsMember>
        )}
        {publisher.timezone && publisher.country && (
          <ListItem>
            {publisher.country && <ListItemText primary="Country" secondary={publisher.country} />}
            {publisher.timezone && <ListItemText primary="Timezone" secondary={publisher.timezone} />}
            {publisher.timezone && (
              <ListItemText primary="Current Time" secondary={<Now timezone={publisher.timezone} />} />
            )}
          </ListItem>
        )}
        <Stack direction="row" spacing={1}>
          {publisher.yearLastGameReleased && (
            <ListItem>
              <ListItemText primary="Year Last Game Released" secondary={publisher.yearLastGameReleased} />
            </ListItem>
          )}
          <IsMember LockedView={<LockedListItem title="Games Published (including expansions)" />}>
            {publisher.gamesCount && publisher.gamesCount !== "" && (
              <ListItem>
                <ListItemText primary="Games Published (including expansions)" secondary={publisher.gamesCount} />
              </ListItem>
            )}
          </IsMember>
        </Stack>
        {isStale(publisher) && (
          <Alert severity="warning">
            Please note this publisher has not published a game since {publisher.yearLastGameReleased}.
          </Alert>
        )}
        <Markdown>{guidelines}</Markdown>
      </List>
    </>
  );
};

/**
 * @param {Object} props
 * @param {import("@seabrookstudios/pitch2table-core").PublisherId} props.publisherId
 */
export const PublisherResearchSummaryBody = ({ publisherId }) => {
  const { data: publisherAndContacts = [], isLoading } = usePublisherAndContacts(publisherId);
  const [publisher] = publisherAndContacts;

  if (isLoading) {
    return <CircularProgress />;
  }

  if (!publisher) {
    return <EmptyDetailCard text="An error occurred loading publisher" />;
  }

  return <PublisherResearchDetailCard publisher={publisher} />;
};
